import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, CartesianGrid, ZAxis,
} from 'recharts';
import {
  BarChart3,
  Calendar,
  Check,
  ChevronDown,
  GitCompareArrows,
  GraduationCap,
  HelpCircle,
  Info,
  Lightbulb,
  MapPin,
  Pin,
  TrendingDown,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import { getImportedMeasurements, isBlankHierarchyField } from '../utils/importedData';
import {
  compareHierarchyToken,
  dedupeHierarchyTokens,
  groupsEqual,
  groupsForPeriodFromStructure,
  normalizeGroupToken,
  normalizePeriodToken,
  periodsEqual,
  periodsFromClassStructure,
} from '../utils/classStructure';
import { REFERENCE_LOCATIONS, getReferenceWeekSeries } from '../utils/referenceTrends';
import { apiRequest } from '../api/http';
import { getColorForValue, getStatusLabel, hasHealthThreshold } from '../utils/airQuality';
import { detectOutliers } from '../utils/outliers';
import SaveChartButton from './charts/SaveChartButton';
import BoxPlot from './charts/BoxPlot';
import ChartFrame from './charts/ChartFrame';
import ReflectionPrompt from './charts/ReflectionPrompt';
import Button from './ui/Button';

/** Shared "good defaults" axis styling: visible axis line + tick line, per the chart-defaults checklist item. */
const AXIS_STYLE = { fontSize: '12px' };
const AXIS_LINE_PROPS = { stroke: '#9CA3AF', style: AXIS_STYLE, tickLine: true, axisLine: { stroke: '#9CA3AF' } };

/** Pins a snapshot of a chart's config/data into the new Workspace tab (Phase 3). */
const SendToWorkspaceButton = ({ onSendToWorkspace, buildItem, className = '' }) => {
  if (!onSendToWorkspace) return null;
  return (
    <button
      type="button"
      data-export-hide="true"
      onClick={() => onSendToWorkspace(buildItem())}
      title="Pin this chart to your Workspace tab"
      className={`inline-flex items-center gap-1.5 rounded-ctrl border border-hairline bg-surface px-2.5 py-1.5 text-cap font-semibold text-secondary transition-colors hover:bg-canvas ${className}`}
    >
      <Pin className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden="true" />
      <span className="leading-none">Send to Workspace</span>
    </button>
  );
};

/** Metrics we try to load from OpenAQ near the reference pin (when a sensor exists). */
const OPENAQ_REFERENCE_METRICS = ['pm25', 'co', 'temp', 'humidity'];

const COMPARISON_PALETTE = ['#3B82F6', '#EF4444', '#10B981', '#8B5CF6', '#F59E0B', '#6366F1', '#EC4899', '#14B8A6'];
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Average `metricKey` per weekday across all matching rows (rows may span several weeks). */
function weekdayAverageSeries(rows, metricKey) {
  const byDay = {};
  rows.forEach((row) => {
    const d = new Date(`${row.date}T${row.time || '00:00'}`);
    if (Number.isNaN(d.getTime())) return;
    const label = WEEKDAY_LABELS[(d.getDay() + 6) % 7];
    const value = Number(row[metricKey]);
    if (!Number.isFinite(value)) return;
    if (!byDay[label]) byDay[label] = { sum: 0, count: 0 };
    byDay[label].sum += value;
    byDay[label].count += 1;
  });
  return WEEKDAY_LABELS.map((label) => ({
    day: label,
    value: byDay[label] ? Number((byDay[label].sum / byDay[label].count).toFixed(2)) : null,
  }));
}

const ComparisonModal = ({
  isOpen,
  onClose,
  selectedMetric,
  theme,
  metricThemes,
  currentFilters,
  workspaceGroups,
  comparisonSchoolCodes,
  importedRows,
}) => {
  const schoolChipList =
    comparisonSchoolCodes?.length > 0
      ? comparisonSchoolCodes
      : [...new Set([currentFilters?.school].filter(Boolean))];
  const groupButtonList =
    workspaceGroups?.length > 0
      ? workspaceGroups
      : ['G1', 'G2', 'G3', 'G4', 'G5', 'G6'];
  const [comparisonType, setComparisonType] = useState('location'); // 'group', 'school', 'location', 'time'
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState(() =>
    REFERENCE_LOCATIONS.slice(0, 3).map((l) => l.name)
  );
  const [timeRange, setTimeRange] = useState('week');
  const comparisonChartRef = useRef(null);

  if (!isOpen) return null;

  const getComparisonData = () => {
    switch (comparisonType) {
      case 'location': {
        if (!selectedLocations.length) return [];
        return selectedLocations.map((loc, idx) => {
          const series = getReferenceWeekSeries(loc, selectedMetric);
          const values = series.map((s) => s.value);
          const rawAvg = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
          const avg =
            selectedMetric === 'co'
              ? Math.round(rawAvg * 100) / 100
              : Math.round(rawAvg);
          return {
            name: loc,
            values,
            avg,
            color: COMPARISON_PALETTE[idx % COMPARISON_PALETTE.length],
          };
        });
      }
      // Uses only rows already loaded in this session/browser (imported CSV + your workspace's
      // synced measurements) — not a live cross-tenant backend query. Crowdsourced comparison
      // across other schools' private data needs a team-level visibility decision first.
      case 'group': {
        const rows = importedRows || [];
        const groupsToShow = selectedGroups.length ? selectedGroups : groupButtonList.slice(0, 3);
        if (!groupsToShow.length) return [];
        return groupsToShow.map((g, idx) => {
          const groupRows = rows.filter((r) => groupsEqual(r.group, g));
          const series = weekdayAverageSeries(groupRows, selectedMetric);
          const values = series.map((s) => s.value ?? 0);
          const present = series.map((s) => s.value).filter((v) => v != null);
          const avg = present.length ? Number((present.reduce((a, b) => a + b, 0) / present.length).toFixed(2)) : 0;
          return { name: `Group ${String(g).replace('G', '')}`, values, avg, color: COMPARISON_PALETTE[idx % COMPARISON_PALETTE.length] };
        });
      }
      case 'school': {
        const rows = importedRows || [];
        const schoolsToShow = selectedSchools.length ? selectedSchools : schoolChipList.slice(0, 3);
        if (!schoolsToShow.length) return [];
        return schoolsToShow.map((s, idx) => {
          const schoolRows = rows.filter((r) => r.school === s);
          const series = weekdayAverageSeries(schoolRows, selectedMetric);
          const values = series.map((d) => d.value ?? 0);
          const present = series.map((d) => d.value).filter((v) => v != null);
          const avg = present.length ? Number((present.reduce((a, b) => a + b, 0) / present.length).toFixed(2)) : 0;
          return { name: s, values, avg, color: COMPARISON_PALETTE[idx % COMPARISON_PALETTE.length] };
        });
      }
      case 'time':
        return [];
      default:
        return [];
    }
  };

  const comparisonData = getComparisonData();
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  // Create chart data
  const chartData = days.map((day, idx) => {
    const dataPoint = { day };
    comparisonData.forEach(item => {
      dataPoint[item.name] = item.values[idx];
    });
    return dataPoint;
  });

  const toggleGroupSelection = (group) => {
    setSelectedGroups(prev => 
      prev.includes(group) 
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };

  const toggleSchoolSelection = (school) => {
    setSelectedSchools(prev => 
      prev.includes(school) 
        ? prev.filter(s => s !== school)
        : [...prev, school]
    );
  };

  const toggleLocationSelection = (location) => {
    setSelectedLocations(prev => 
      prev.includes(location) 
        ? prev.filter(l => l !== location)
        : [...prev, location]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-surface rounded-card max-w-6xl w-full shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-hairline-soft flex items-center justify-between sticky top-0 z-10 bg-surface rounded-t-card">
          <div>
            <h3 className="text-tile text-fg">Compare data — {metricThemes[selectedMetric].label}</h3>
            <p className="text-small text-secondary mt-1">Compare across groups, schools, locations, and time periods</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-2 text-muted hover:text-fg hover:bg-canvas rounded-ctrl transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6">
          {/* Comparison Type Selector — a plain segmented row, same chip language as the
              metric selector above, instead of a grid of centered-icon color tiles. */}
          <div className="mb-6">
            <p className="text-cap font-semibold uppercase tracking-wide text-muted mb-2">Comparison type</p>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'group', label: 'By Group', Icon: Users },
                { id: 'school', label: 'By School', Icon: GraduationCap },
                { id: 'location', label: 'By Location', Icon: MapPin },
                { id: 'time', label: 'By Time', Icon: Calendar }
              ].map((type) => (
                <button
                  key={type.id}
                  onClick={() => setComparisonType(type.id)}
                  className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-pill text-small font-medium border transition-colors ${
                    comparisonType === type.id
                      ? `${theme.bg} text-white border-transparent`
                      : 'bg-surface text-secondary border-hairline hover:bg-canvas'
                  }`}
                >
                  <type.Icon className="h-4 w-4" aria-hidden="true" />
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Selection Panel */}
          <div className="mb-6 bg-canvas rounded-card p-4 border border-hairline-soft">
            {comparisonType === 'group' && (
              <div>
                <h4 className="text-small font-semibold text-secondary mb-3">Select Groups to Compare</h4>
                <div className="flex flex-wrap gap-2">
                  {groupButtonList.map((group) => (
                    <button
                      key={group}
                      onClick={() => toggleGroupSelection(group)}
                      className={`px-4 py-2 rounded-pill text-small font-medium border transition-colors ${
                        selectedGroups.includes(group)
                          ? `${theme.bg} text-white border-transparent`
                          : 'bg-surface text-secondary border-hairline hover:bg-canvas'
                      }`}
                    >
                      Group {group.replace('G', '')}
                    </button>
                  ))}
                </div>
                <p className="text-cap text-muted mt-2">Currently viewing: {currentFilters.school} - Your group is G{currentFilters.group.replace('G', '')}</p>
              </div>
            )}

            {comparisonType === 'school' && (
              <div>
                <h4 className="text-small font-semibold text-secondary mb-3">Select Schools to Compare</h4>
                <div className="flex flex-wrap gap-2">
                  {schoolChipList.map((school) => (
                    <button
                      key={school}
                      onClick={() => toggleSchoolSelection(school)}
                      className={`px-4 py-2 rounded-pill text-small font-medium border transition-colors ${
                        selectedSchools.includes(school)
                          ? `${theme.bg} text-white border-transparent`
                          : 'bg-surface text-secondary border-hairline hover:bg-canvas'
                      }`}
                    >
                      {school}
                    </button>
                  ))}
                </div>
                <p className="text-cap text-muted mt-2">Your school: {currentFilters.school}</p>
              </div>
            )}

            {comparisonType === 'location' && (
              <div>
                <h4 className="text-small font-semibold text-secondary mb-3">Select Locations to Compare</h4>
                <div className="flex flex-wrap gap-2">
                  {REFERENCE_LOCATIONS.map((ref) => (
                    <button
                      key={ref.name}
                      onClick={() => toggleLocationSelection(ref.name)}
                      className={`px-4 py-2 rounded-pill text-small font-medium border transition-colors ${
                        selectedLocations.includes(ref.name)
                          ? `${theme.bg} text-white border-transparent`
                          : 'bg-surface text-secondary border-hairline hover:bg-canvas'
                      }`}
                    >
                      {ref.name}
                    </button>
                  ))}
                </div>
                <p className="text-cap text-muted mt-2">
                  Curves use the same Philadelphia, New York, and Hanoi city references as Analysis.
                </p>
              </div>
            )}

            {comparisonType === 'time' && (
              <div>
                <h4 className="text-small font-semibold text-secondary mb-3">Select Time Period</h4>
                <div className="flex gap-2">
                  {['week', 'month', 'year'].map(period => (
                    <button
                      key={period}
                      onClick={() => setTimeRange(period)}
                      className={`px-4 py-2 rounded-pill text-small font-medium border transition-colors ${
                        timeRange === period
                          ? `${theme.bg} text-white border-transparent`
                          : 'bg-surface text-secondary border-hairline hover:bg-canvas'
                      }`}
                    >
                      {period.charAt(0).toUpperCase() + period.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {(comparisonType === 'group' || comparisonType === 'school') && (
            <p className="mb-4 text-small text-secondary rounded-ctrl border border-hairline-soft bg-canvas px-4 py-3">
              Weekday averages from measurements already loaded in this session (your imported CSV / synced
              workspace data) — not a live query of other schools' private data. Cross-school crowdsourced
              comparisons need a shared-visibility decision from the team first.
            </p>
          )}
          {comparisonType === 'time' && (
            <p className="mb-4 text-small text-secondary rounded-ctrl border border-hairline-soft bg-canvas px-4 py-3">
              Time-period comparisons are not wired to real data yet. Use <strong className="text-fg font-medium">By Location</strong>,{' '}
              <strong className="text-fg font-medium">By Group</strong>, or <strong className="text-fg font-medium">By School</strong> instead.
            </p>
          )}

          {/* Comparison Chart */}
          <div ref={comparisonChartRef} className="bg-surface rounded-card p-6 border border-hairline-soft mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-tile text-fg">Comparison Visualization</h4>
              {comparisonData.length > 0 && (
                <SaveChartButton
                  targetRef={comparisonChartRef}
                  filename={`${metricThemes[selectedMetric].label}-${metricThemes[selectedMetric].unit}-comparison-by-${comparisonType}`}
                />
              )}
            </div>
            {comparisonData.length === 0 ? (
              <div className="flex h-[400px] items-center justify-center text-center text-small text-muted px-6">
                {comparisonType === 'location'
                  ? 'Select at least one city above to plot reference trends.'
                  : comparisonType === 'time'
                    ? 'No chart for this comparison type until workspace data is wired in.'
                    : 'Select at least one group/school above, or add more imported data, to plot a comparison.'}
              </div>
            ) : (
              <ChartFrame
                height={360}
                xLabel="Day"
                yLabel={metricThemes[selectedMetric].unit}
                legendItems={comparisonData.map((item) => ({ label: item.name, color: item.color }))}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="day" {...AXIS_LINE_PROPS} />
                    <YAxis {...AXIS_LINE_PROPS} />
                    <Tooltip
                      contentStyle={{
                        background: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        padding: '12px',
                      }}
                    />
                    {comparisonData.map((item, idx) => (
                      <Line
                        key={idx}
                        type="monotone"
                        dataKey={item.name}
                        stroke={item.color}
                        strokeWidth={2}
                        dot={{ fill: item.color, r: 4 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </ChartFrame>
            )}
          </div>

          {/* Comparison Statistics Table */}
          <div className="bg-surface rounded-card border border-hairline-soft overflow-hidden">
            <h4 className="text-tile text-fg p-4 border-b border-hairline-soft">Statistical Comparison</h4>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-canvas">
                  <tr>
                    <th className="px-4 py-3 text-left text-small font-semibold text-secondary">Name</th>
                    <th className="px-4 py-3 text-left text-small font-semibold text-secondary">Average</th>
                    <th className="px-4 py-3 text-left text-small font-semibold text-secondary">Min</th>
                    <th className="px-4 py-3 text-left text-small font-semibold text-secondary">Max</th>
                    <th className="px-4 py-3 text-left text-small font-semibold text-secondary">Range</th>
                    <th className="px-4 py-3 text-left text-small font-semibold text-secondary">Trend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hairline-soft">
                  {comparisonData.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-small text-muted">
                        No comparison rows. Choose locations (reference series) or another view with real data.
                      </td>
                    </tr>
                  ) : (
                    comparisonData.map((item, idx) => {
                      const min = Math.min(...item.values);
                      const max = Math.max(...item.values);
                      const trend =
                        item.values[item.values.length - 1] > item.values[0] ? 'increasing' : 'decreasing';

                      return (
                        <tr key={idx} className="hover:bg-canvas">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                              <span className="font-medium text-fg">{item.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-fg font-semibold">{item.avg}</td>
                          <td className="px-4 py-3 text-fg font-semibold">{min}</td>
                          <td className="px-4 py-3 text-fg font-semibold">{max}</td>
                          <td className="px-4 py-3 text-secondary">{max - min}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-canvas border border-hairline text-secondary text-cap font-semibold rounded-pill">
                              {trend === 'increasing' ? (
                                <TrendingUp className="w-3 h-3" aria-hidden="true" />
                              ) : (
                                <TrendingDown className="w-3 h-3" aria-hidden="true" />
                              )}
                              {trend === 'increasing' ? 'Rising' : 'Falling'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Key Insights — plain card + accent bullet dots, matching the Overview tab's
              Key Insights treatment, instead of a filled blue callout. */}
          {comparisonData.length > 0 && (
            <div className="mt-6 bg-surface rounded-card p-4 border border-hairline-soft">
              <h4 className="mb-2 flex items-center gap-2 text-small font-semibold text-fg">
                <Lightbulb className="h-4 w-4" style={{ color: theme.primary }} aria-hidden="true" />
                Key Insights
              </h4>
              <ul className="text-small text-secondary space-y-1.5">
                <li className="flex items-start gap-2">
                  <span style={{ color: theme.primary }}>•</span>
                  <span>
                    Highest average:{' '}
                    <strong className="text-fg font-medium">
                      {comparisonData.reduce((max, item) => (item.avg > max.avg ? item : max), comparisonData[0]).name}
                    </strong>{' '}
                    (
                    {comparisonData.reduce((max, item) => (item.avg > max.avg ? item : max), comparisonData[0]).avg}{' '}
                    {metricThemes[selectedMetric].unit})
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: theme.primary }}>•</span>
                  <span>
                    Lowest average:{' '}
                    <strong className="text-fg font-medium">
                      {comparisonData.reduce((min, item) => (item.avg < min.avg ? item : min), comparisonData[0]).name}
                    </strong>{' '}
                    (
                    {comparisonData.reduce((min, item) => (item.avg < min.avg ? item : min), comparisonData[0]).avg}{' '}
                    {metricThemes[selectedMetric].unit})
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: theme.primary }}>•</span>
                  <span>
                    Range across series:{' '}
                    {Math.max(...comparisonData.map((d) => d.avg)) - Math.min(...comparisonData.map((d) => d.avg))}{' '}
                    {metricThemes[selectedMetric].unit}
                  </span>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AnalysisView = ({
  selectedMetric,
  setSelectedMetric,
  filters,
  theme,
  metricThemes,
  importedDataVersion,
  classStructure,
  onSendToWorkspace,
  userRole = 'student',
  onStartTour,
}) => {
  const isTeacher = userRole === 'teacher';
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showReferenceInfo, setShowReferenceInfo] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'compare'
  const [compareMode, setCompareMode] = useState('openaq'); // openaq | group | class | school
  const [compareGroup, setCompareGroup] = useState('');
  const [referenceLocation, setReferenceLocation] = useState(REFERENCE_LOCATIONS[0]?.name || 'Philadelphia');
  const [openaqPoints, setOpenaqPoints] = useState(null);
  const [openaqMeta, setOpenaqMeta] = useState({ status: 'idle', message: '' });
  // Teachers see every period/group in the class — focus controls narrow Analysis immersion.
  const [focusPeriod, setFocusPeriod] = useState(filters.period || 'all');
  const [focusGroup, setFocusGroup] = useState(filters.group || 'all');
  const [openSections, setOpenSections] = useState({
    recent: true,
    trends: true,
    distribution: false,
    box: false,
    scatter: false,
    insights: false,
  });
  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const imported = useMemo(
    () => getImportedMeasurements(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [importedDataVersion]
  );

  const softEq = (filterVal, rowVal) => {
    if (isBlankHierarchyField(filterVal) || isBlankHierarchyField(rowVal)) return true;
    return String(filterVal).trim() === String(rowVal).trim();
  };

  const softPeriodEq = (filterVal, rowVal) => {
    if (isBlankHierarchyField(filterVal) || isBlankHierarchyField(rowVal)) return true;
    return periodsEqual(filterVal, rowVal);
  };

  const softGroupEq = (filterVal, rowVal) => {
    if (isBlankHierarchyField(filterVal) || isBlankHierarchyField(rowVal)) return true;
    return groupsEqual(filterVal, rowVal);
  };

  const focusPeriodOptions = useMemo(() => {
    const fromData = imported.map((row) => String(row.period || '').trim()).filter(Boolean);
    const fromStructure = periodsFromClassStructure(classStructure);
    return dedupeHierarchyTokens([...fromStructure, ...fromData], 'period');
  }, [imported, classStructure]);

  const focusGroupOptions = useMemo(() => {
    const periodFilter = focusPeriod === 'all' ? '' : focusPeriod;
    const fromData = imported
      .filter((row) => !periodFilter || softPeriodEq(periodFilter, row.period))
      .map((row) => String(row.group || '').trim())
      .filter(Boolean);
    const fromStructure = groupsForPeriodFromStructure(
      classStructure,
      periodFilter || filters.period
    );
    return dedupeHierarchyTokens([...fromStructure, ...fromData], 'group');
  }, [imported, classStructure, focusPeriod, filters.period]);

  useEffect(() => {
    if (focusPeriod === 'all') return;
    const canon = normalizePeriodToken(focusPeriod);
    const match = focusPeriodOptions.find((p) => normalizePeriodToken(p) === canon);
    if (match && match !== focusPeriod) setFocusPeriod(match);
    else if (focusPeriodOptions.length && !match) setFocusPeriod('all');
  }, [focusPeriod, focusPeriodOptions]);

  useEffect(() => {
    if (focusGroup === 'all') return;
    const canon = normalizeGroupToken(focusGroup);
    const match = focusGroupOptions.find((g) => normalizeGroupToken(g) === canon);
    if (match && match !== focusGroup) setFocusGroup(match);
    else if (focusGroupOptions.length && !match) setFocusGroup('all');
  }, [focusGroup, focusGroupOptions]);

  const scopedData = useMemo(() => {
    const pool = imported.length ? imported : [];
    if (isTeacher) {
      return pool.filter((row) => {
        if (!softEq(filters.instructor, row.instructor)) return false;
        if (focusPeriod !== 'all' && !softPeriodEq(focusPeriod, row.period)) return false;
        if (focusGroup !== 'all' && !softGroupEq(focusGroup, row.group)) return false;
        return true;
      });
    }
    const filtered = pool.filter((row) => {
      // School name is intentionally not filtered — CSV codes vs directory names
      // were emptying Analysis while Raw Data still showed rows.
      if (!softEq(filters.instructor, row.instructor)) return false;
      if (!softPeriodEq(filters.period, row.period)) return false;
      if (!softGroupEq(filters.group, row.group)) return false;
      return true;
    });
    // Students: if profile filters match nothing but the workspace cache has rows, still analyze.
    return filtered.length ? filtered : pool;
  }, [
    imported,
    isTeacher,
    filters.instructor,
    filters.period,
    filters.group,
    focusPeriod,
    focusGroup,
  ]);

  const hasData = scopedData.length > 0;

  const classScopeData = useMemo(() => {
    const filtered = imported.filter((row) => {
      if (!softEq(filters.instructor, row.instructor)) return false;
      if (isTeacher) {
        if (focusPeriod !== 'all' && !softPeriodEq(focusPeriod, row.period)) return false;
        return true;
      }
      if (!softPeriodEq(filters.period, row.period)) return false;
      return true;
    });
    return isTeacher ? filtered : filtered.length ? filtered : imported;
  }, [imported, filters.instructor, filters.period, isTeacher, focusPeriod]);

  const schoolScopeData = useMemo(() => {
    if (!isTeacher) return imported;
    return imported.filter((row) => softEq(filters.instructor, row.instructor));
  }, [imported, isTeacher, filters.instructor]);

  const monthData = useMemo(() => {
    if (!scopedData.length) return [];
    const byDate = {};
    scopedData.forEach((row) => {
      const key = row.date;
      const value = Number(row[selectedMetric] ?? 0);
      if (!byDate[key]) byDate[key] = { sum: 0, count: 0 };
      byDate[key].sum += value;
      byDate[key].count += 1;
    });
    return Object.entries(byDate)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .map(([date, agg]) => ({ date, value: Number((agg.sum / agg.count).toFixed(2)) }));
  }, [scopedData, selectedMetric]);

  const weekData = useMemo(() => {
    if (!monthData.length) return [];
    return monthData.slice(-7).map((d) => ({
      day: new Date(`${d.date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short' }),
      value: d.value,
      date: d.date,
    }));
  }, [monthData]);

  const openaqByDate = useMemo(() => {
    if (!openaqPoints?.length) return null;
    return Object.fromEntries(openaqPoints.map((p) => [p.date, p.value]));
  }, [openaqPoints]);

  const openaqPointsSorted = useMemo(
    () => [...(openaqPoints || [])].sort((a, b) => String(a.date).localeCompare(String(b.date))),
    [openaqPoints]
  );

  useEffect(() => {
    if (!weekData.length || !OPENAQ_REFERENCE_METRICS.includes(selectedMetric)) {
      setOpenaqPoints(null);
      setOpenaqMeta({ status: 'idle', message: '' });
      return;
    }
    const loc = REFERENCE_LOCATIONS.find((l) => l.name === referenceLocation);
    if (loc?.lat == null || loc?.lng == null) return;

    let cancelled = false;
    (async () => {
      setOpenaqMeta({ status: 'loading', message: '' });
      try {
        const dateFrom = weekData[0].date;
        const dateTo = weekData[weekData.length - 1].date;
        const q = new URLSearchParams({
          lat: String(loc.lat),
          lng: String(loc.lng),
          date_from: dateFrom,
          date_to: dateTo,
          metric: selectedMetric,
        });
        const data = await apiRequest(`/analytics/openaq/daily?${q.toString()}`);
        if (cancelled) return;
        if (data.error === 'no_sensor' || !(data.points || []).length) {
          setOpenaqPoints(null);
          setOpenaqMeta({
            status: 'error',
            message:
              selectedMetric === 'co'
                ? 'No CO reference near this pin (WAQI fallback is PM2.5 / temp / humidity) — using simulated reference.'
                : (data.message || 'No reference sensor for this metric near the selected pin — using simulated reference.'),
          });
          return;
        }
        setOpenaqPoints(data.points || []);
        const sourceLabel = data.source === 'waqi' ? 'WAQI' : 'OpenAQ';
        const base = data.locationName ? `${sourceLabel} @ ${data.locationName}` : sourceLabel;
        const alignedNote =
          data.source === 'waqi' && data.dateMatch === 'nearest_window'
            ? ' · recent WAQI days aligned to your week'
            : '';
        setOpenaqMeta({ status: 'ok', message: `${base}${alignedNote}` });
      } catch (e) {
        if (cancelled) return;
        setOpenaqPoints(null);
        setOpenaqMeta({
          status: 'error',
          message: e?.message || 'Reference data unavailable — using simulated reference.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [weekData, referenceLocation, selectedMetric]);

  /** Your week vs OpenAQ/WAQI when available; else simulated reference. */
  const weekCompareData = useMemo(() => {
    if (!weekData.length) return [];
    const sim = getReferenceWeekSeries(referenceLocation, selectedMetric);
    const exactHits = openaqByDate
      ? weekData.filter((row) => openaqByDate[row.date] != null).length
      : 0;
    // WAQI forecast window often won't share calendar dates with an older class CSV week.
    const alignByIndex =
      OPENAQ_REFERENCE_METRICS.includes(selectedMetric) &&
      openaqPointsSorted.length > 0 &&
      exactHits === 0;

    return weekData.map((row, i) => {
      let reference;
      if (
        OPENAQ_REFERENCE_METRICS.includes(selectedMetric) &&
        openaqByDate &&
        openaqByDate[row.date] != null
      ) {
        reference = openaqByDate[row.date];
      } else if (alignByIndex) {
        const idx = Math.min(i, openaqPointsSorted.length - 1);
        reference = openaqPointsSorted[idx].value;
      } else {
        reference = sim[i]?.value ?? sim[sim.length - 1]?.value;
      }
      return {
        label: row.day,
        yours: row.value,
        reference,
      };
    });
  }, [weekData, referenceLocation, selectedMetric, openaqByDate, openaqPointsSorted]);

  const stats = useMemo(() => {
    const allValues = monthData.map((d) => Number(d.value));
    if (!allValues.length) return null;
    const avgValue = Math.round(allValues.reduce((sum, val) => sum + val, 0) / allValues.length);
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const sortedValues = [...allValues].sort((a, b) => a - b);
    const medianValue = sortedValues[Math.floor(sortedValues.length / 2)];
    const standardDeviation = Math.sqrt(
      allValues.reduce((sum, val) => sum + Math.pow(val - avgValue, 2), 0) / allValues.length
    ).toFixed(2);
    return { avgValue, minValue, maxValue, medianValue, standardDeviation, allValues };
  }, [monthData]);

  const classAverage = useMemo(() => {
    const schoolRows = classScopeData;
    if (!schoolRows.length) return null;
    return Math.round(
      schoolRows.reduce((sum, row) => sum + Number(row[selectedMetric] || 0), 0) / schoolRows.length
    );
  }, [classScopeData, selectedMetric]);

  const schoolAverage = useMemo(() => {
    const schoolRows = schoolScopeData;
    if (!schoolRows.length) return null;
    return Math.round(
      schoolRows.reduce((sum, row) => sum + Number(row[selectedMetric] || 0), 0) / schoolRows.length
    );
  }, [schoolScopeData, selectedMetric]);

  const availableCompareGroups = useMemo(() => {
    const fromData = classScopeData.map((r) => r.group).filter(Boolean);
    const period = filters.period || periodsFromClassStructure(classStructure)[0];
    const fromWorkspace = groupsForPeriodFromStructure(classStructure, period);
    const merged = dedupeHierarchyTokens([...fromWorkspace, ...fromData], 'group');
    return merged.filter((g) => !groupsEqual(g, filters.group));
  }, [classScopeData, filters.group, filters.period, classStructure]);

  const workspaceGroupsForCompare = useMemo(() => {
    const p = filters.period || periodsFromClassStructure(classStructure)[0];
    const g = groupsForPeriodFromStructure(classStructure, p);
    if (g.length) return g;
    return ['G1', 'G2', 'G3', 'G4', 'G5', 'G6'];
  }, [classStructure, filters.period]);

  const comparisonSchoolCodes = useMemo(() => {
    const fromFile = [...new Set(imported.map((r) => r.school).filter(Boolean))];
    const merged = [...new Set([filters.school, ...fromFile])].filter(Boolean);
    return merged.sort();
  }, [imported, filters.school]);

  useEffect(() => {
    if (!availableCompareGroups.length) {
      setCompareGroup('');
      return;
    }
    if (!compareGroup || !availableCompareGroups.some((g) => groupsEqual(g, compareGroup))) {
      setCompareGroup(availableCompareGroups[0]);
    }
  }, [availableCompareGroups, compareGroup]);

  const compareChartData = useMemo(() => {
    if (!weekData.length) return [];
    const makeDailySeries = (rows) => {
      const byDate = {};
      rows.forEach((row) => {
        const key = row.date;
        const value = Number(row[selectedMetric] ?? 0);
        if (!byDate[key]) byDate[key] = { sum: 0, count: 0 };
        byDate[key].sum += value;
        byDate[key].count += 1;
      });
      return Object.entries(byDate)
        .sort((a, b) => new Date(a[0]) - new Date(b[0]))
        .map(([date, agg]) => ({ date, value: Number((agg.sum / agg.count).toFixed(2)) }));
    };

    if (compareMode === 'openaq') {
      return weekCompareData.map((d) => ({ day: d.label, yours: d.yours, comparison: d.reference }));
    }

    const dayKeys = weekData.map((d) => d.date);
    let comparisonSeries = [];
    if (compareMode === 'group' && compareGroup) {
      comparisonSeries = makeDailySeries(classScopeData.filter((r) => groupsEqual(r.group, compareGroup)));
    } else if (compareMode === 'class') {
      comparisonSeries = makeDailySeries(classScopeData);
    } else if (compareMode === 'school') {
      comparisonSeries = makeDailySeries(schoolScopeData);
    }
    const comparisonByDate = Object.fromEntries(comparisonSeries.map((d) => [d.date, d.value]));

    return weekData
      .filter((d) => dayKeys.includes(d.date))
      .map((d) => ({
        day: d.day,
        yours: d.value,
        comparison: comparisonByDate[d.date] ?? null,
      }));
  }, [weekData, weekCompareData, compareMode, compareGroup, classScopeData, schoolScopeData, selectedMetric]);

  const avgValue = stats?.avgValue ?? 0;
  const minValue = stats?.minValue ?? 0;
  const maxValue = stats?.maxValue ?? 0;
  const medianValue = stats?.medianValue ?? 0;
  const allValues = stats?.allValues ?? [];

  // Outlier / surprising-reading spotting (IQR-based) — a callout list, not a stats test.
  const dailyOutliers = useMemo(
    () => detectOutliers(monthData, (d) => d.value),
    [monthData]
  );

  // Box plot groups: distribution of the selected metric per team in the current class period.
  const boxPlotGroups = useMemo(() => {
    const byGroup = {};
    classScopeData.forEach((row) => {
      const key = normalizeGroupToken(row.group) || 'Ungrouped';
      const value = Number(row[selectedMetric]);
      if (!Number.isFinite(value)) return;
      if (!byGroup[key]) byGroup[key] = [];
      byGroup[key].push(value);
    });
    return Object.entries(byGroup)
      .sort((a, b) => compareHierarchyToken(a[0], b[0]))
      .map(([label, values]) => ({ label, values }));
  }, [classScopeData, selectedMetric]);

  // Bivariate / scatter — pick a second metric to plot against the selected one.
  const [scatterMetric, setScatterMetric] = useState(
    () => Object.keys(metricThemes).find((k) => k !== selectedMetric) || selectedMetric
  );
  useEffect(() => {
    if (scatterMetric === selectedMetric) {
      const alt = Object.keys(metricThemes).find((k) => k !== selectedMetric);
      if (alt) setScatterMetric(alt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMetric]);
  const scatterData = useMemo(
    () =>
      scopedData
        .map((row) => ({
          x: Number(row[selectedMetric]),
          y: Number(row[scatterMetric]),
          group: row.group || 'Ungrouped',
        }))
        .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y)),
    [scopedData, selectedMetric, scatterMetric]
  );

  // Refs for per-chart "Save image" buttons (2c) and the sticky context header (2a).
  const weekChartRef = useRef(null);
  const monthChartRef = useRef(null);
  const distributionChartRef = useRef(null);
  const boxPlotRef = useRef(null);
  const scatterChartRef = useRef(null);
  const quickCompareChartRef = useRef(null);

  return (
    <div className="space-y-6">
      {/* Header + view tabs */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <h1 className="text-page text-fg">Analysis</h1>
          <button
            type="button"
            onClick={() => {
              setActiveTab('overview');
              onStartTour?.();
            }}
            className="inline-flex items-center gap-1.5 h-7 px-2.5 text-cap font-semibold text-secondary rounded-pill border border-hairline bg-surface hover:bg-canvas transition-colors"
          >
            <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
            How to
          </button>
        </div>
        <div className="seg inline-flex rounded-pill border border-hairline bg-surface p-[3px] gap-0.5">
          <button
            onClick={() => setActiveTab('overview')}
            aria-pressed={activeTab === 'overview'}
            className={`flex items-center gap-2 px-4 h-9 rounded-pill text-small transition-all ${
              activeTab === 'overview' ? 'bg-fg text-white' : 'text-secondary hover:bg-canvas'
            }`}
          >
            <BarChart3 className="h-4 w-4" aria-hidden="true" />
            Overview
          </button>
          <button
            data-tour="compare-tab"
            onClick={() => setActiveTab('compare')}
            aria-pressed={activeTab === 'compare'}
            className={`flex items-center gap-2 px-4 h-9 rounded-pill text-small transition-all ${
              activeTab === 'compare' ? 'bg-fg text-white' : 'text-secondary hover:bg-canvas'
            }`}
          >
            <GitCompareArrows className="h-4 w-4" aria-hidden="true" />
            Compare Data
          </button>
        </div>
      </div>

      {/* Compact sticky context + section rail. It replaces the large metric card and keeps
          navigation available without adding another page-level tab row. */}
      {(hasData || isTeacher) && (
        <div className="filters sticky top-20 z-30 rounded-card border border-hairline-soft bg-surface/95 px-3 py-2.5 backdrop-blur space-y-2">
          <div className="frow flex flex-wrap items-center gap-2">
            <div data-tour="metric-chips" className="flex flex-wrap items-center gap-2">
              {Object.entries(metricThemes).map(([key, metric]) => (
                <button
                  key={key}
                  onClick={() => setSelectedMetric(key)}
                  className={`chip h-8 px-3.5 rounded-pill text-small border transition-colors ${
                    selectedMetric === key
                      ? `${metric.bg} text-white border-transparent`
                      : 'bg-surface text-secondary border-hairline hover:bg-canvas'
                  }`}
                >
                  {metric.label}
                </button>
              ))}
              {hasData && hasHealthThreshold(selectedMetric) && (
                <span
                  className="badge inline-flex items-center gap-1.5 h-8 px-3 rounded-pill text-cap font-semibold"
                  style={{ backgroundColor: getColorForValue(avgValue, selectedMetric), color: '#1F2937' }}
                >
                  Avg: {getStatusLabel(avgValue, selectedMetric)}
                </span>
              )}
            </div>
            {/* Period/Group focus — same row as the metric chips, just set off with a
                hairline divider instead of pushed to its own row. */}
            {isTeacher && (
              <div data-tour="period-group" className="ml-auto flex flex-wrap items-center gap-3 pl-3 border-l border-hairline-soft">
                <label className="relative z-20 flex items-center gap-1.5 text-small text-secondary">
                  <span className="flabel shrink-0">Period</span>
                  <select
                    value={focusPeriod}
                    onChange={(e) => {
                      setFocusPeriod(e.target.value);
                      setFocusGroup('all');
                    }}
                    className="sel-sm min-w-[7.5rem] h-9 rounded-ctrl border border-hairline bg-surface px-2.5 text-small text-fg"
                    aria-label="Focus period for analysis"
                  >
                    <option value="all">All periods</option>
                    {focusPeriodOptions.map((period) => (
                      <option key={period} value={period}>{period}</option>
                    ))}
                  </select>
                </label>
                <label className="relative z-20 flex items-center gap-1.5 text-small text-secondary">
                  <span className="flabel shrink-0">Group</span>
                  <select
                    value={focusGroup}
                    onChange={(e) => setFocusGroup(e.target.value)}
                    className="sel-sm min-w-[7.5rem] h-9 rounded-ctrl border border-hairline bg-surface px-2.5 text-small text-fg"
                    aria-label="Focus group for analysis"
                  >
                    <option value="all">All groups</option>
                    {focusGroupOptions.map((group) => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>
          {/* Second row: how you're viewing the data (sections) — kept separate since it's a
              different kind of control (which charts render) than the filters above. */}
          {activeTab === 'overview' && hasData && (
            <div data-tour="view-toggle" className="frow flex flex-wrap items-center gap-1.5">
              <span className="flabel text-small text-muted shrink-0">View</span>
              <div className="segwide flex flex-wrap gap-1 rounded-pill border border-hairline bg-canvas p-1">
                {[
                  ['recent', 'Recent'],
                  ['trends', 'Trends'],
                  ['distribution', 'Distribution'],
                  ['box', 'Box plot'],
                  ['scatter', 'Scatter'],
                  ['insights', 'Insights'],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleSection(key)}
                    aria-expanded={openSections[key]}
                    className={`flex items-center gap-1 rounded-pill px-2.5 h-7 text-cap font-semibold transition-colors ${
                      openSections[key] ? 'bg-fg text-white' : 'text-secondary hover:bg-surface'
                    }`}
                  >
                    {label}
                    <ChevronDown className={`h-3 w-3 transition-transform ${openSections[key] ? 'rotate-180' : ''}`} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Conditional Content based on Active Tab */}
      {!hasData ? (
        <div className="bg-surface rounded-card p-12 border border-hairline-soft text-center max-w-2xl mx-auto">
          <div className="mx-auto w-16 h-16 bg-canvas rounded-full flex items-center justify-center mb-4">
            <MapPin className="w-8 h-8 text-muted" />
          </div>
          <h2 className="text-tile text-fg mb-2">No data for Analysis yet</h2>
          <p className="text-secondary mb-4">
            With your current filters, there are no measurements. Collect sessions in the field or import CSV on{' '}
            <strong>Raw Data</strong>, then come back here.
          </p>
          <p className="text-small text-muted">
            We no longer show placeholder charts — the Analysis page only uses <strong>your</strong> workspace data.
            When you have data, you can compare it to Philadelphia, New York, or Hanoi reference trends.
          </p>
        </div>
      ) : activeTab === 'overview' ? (
        <>
          {/* One thin summary strip instead of four oversized cards. */}
          <div className="flex flex-wrap items-center divide-x divide-hairline-soft rounded-card border border-hairline-soft bg-surface px-2 py-2">
            {[
              ['Average', avgValue, theme.primary],
              ['Median', Math.round(medianValue), '#9333EA'],
              ['Minimum', Math.round(minValue), '#16A34A'],
              ['Maximum', Math.round(maxValue), '#EA580C'],
            ].map(([label, value, color]) => (
              <div key={label} className="flex min-w-[130px] flex-1 items-baseline justify-between gap-2 px-4 py-1">
                <span className="text-cap font-bold uppercase tracking-wider text-muted">{label}</span>
                <span className="text-lg font-bold" style={{ color }}>
                  {value} <span className="text-cap font-medium text-muted">{metricThemes[selectedMetric].unit}</span>
                </span>
              </div>
            ))}
          </div>

      {/* Outlier / surprising-reading callouts — a plain card like any other on this page,
          not a colored alert block/stripe. */}
      {dailyOutliers.length > 0 && (
        <div className="bg-surface border border-hairline-soft rounded-card p-4">
          <h3 className="text-small font-semibold text-fg">Surprising readings to investigate</h3>
          <ul className="text-small text-secondary space-y-1 mt-2">
            {dailyOutliers.slice(0, 5).map((o, idx) => (
              <li key={idx}>
                {o.point.date}: {o.value} {metricThemes[selectedMetric].unit} — unusually{' '}
                {o.direction === 'high' ? 'high' : 'low'} compared to the rest of this series.
              </li>
            ))}
          </ul>
          <p className="text-cap text-muted mt-2">
            Flagged using the 1.5×IQR rule — a starting point for discussion, not a definitive error.
          </p>
        </div>
      )}

      {/* Charts Grid — your recent week vs reference; your full series */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {openSections.recent && (
        <div ref={weekChartRef} className="bg-surface rounded-card p-6 border border-hairline-soft">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-2">
              <h2 className="text-tile text-fg">
                Recent week vs {referenceLocation} ({metricThemes[selectedMetric].label})
              </h2>
              <div className="relative">
                <button
                  type="button"
                  aria-label="About this comparison"
                  aria-expanded={showReferenceInfo}
                  aria-controls="reference-comparison-info"
                  onClick={() => setShowReferenceInfo((visible) => !visible)}
                  className="mt-0.5 rounded-full p-1 text-muted hover:bg-canvas hover:text-secondary"
                >
                  <Info className="h-4 w-4" />
                </button>
                {showReferenceInfo && (
                  <div
                    id="reference-comparison-info"
                    role="note"
                    className="absolute left-0 top-8 z-20 w-72 rounded-ctrl border border-hairline-soft bg-surface p-3 text-cap leading-relaxed text-secondary shadow-xl"
                  >
                    <strong>Your data</strong> uses the current measurement filters. The reference line uses OpenAQ daily
                    averages when a matching sensor exists; otherwise it shows a simulated regional curve.
                  </div>
                )}
              </div>
            </div>
            <div data-tour="send-to-workspace" className="flex shrink-0 flex-wrap items-center justify-end gap-1.5" data-export-hide="true">
              <SaveChartButton
                targetRef={weekChartRef}
                filename={`recent-week-vs-${referenceLocation}-${metricThemes[selectedMetric].label}`}
              />
              <SendToWorkspaceButton
                onSendToWorkspace={onSendToWorkspace}
                buildItem={() => ({
                  id: `week-ref-${selectedMetric}-${Date.now()}`,
                  title: `Recent week vs ${referenceLocation} (${metricThemes[selectedMetric].label})`,
                  subtitle: `${metricThemes[selectedMetric].unit} · current filters`,
                  kind: 'line',
                  data: weekCompareData.map((d) => ({ label: d.label, yours: d.yours, reference: d.reference })),
                  xKey: 'label',
                  xLabel: 'Day',
                  yLabel: metricThemes[selectedMetric].unit,
                  series: [
                    { dataKey: 'yours', label: 'Your data', color: theme.primary },
                    { dataKey: 'reference', label: 'Reference', color: '#94a3b8' },
                  ],
                })}
              />
            </div>
          </div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-t border-hairline-soft pt-3" data-export-hide="true">
            <label className="flex items-center gap-2 text-cap font-medium text-secondary">
              <MapPin className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
              <span className="shrink-0">Reference location</span>
              <select
                value={referenceLocation}
                onChange={(e) => setReferenceLocation(e.target.value)}
                className="h-9 max-w-[220px] rounded-ctrl border border-hairline bg-surface px-3 text-small leading-normal text-fg"
              >
                {REFERENCE_LOCATIONS.map((loc) => (
                  <option key={loc.name} value={loc.name}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </label>
            {OPENAQ_REFERENCE_METRICS.includes(selectedMetric) && openaqMeta.status === 'loading' && (
              <p className="text-cap text-blue-600">Loading OpenAQ reference…</p>
            )}
            {OPENAQ_REFERENCE_METRICS.includes(selectedMetric) && openaqMeta.status === 'ok' && (
              <p className="text-cap text-green-700">{openaqMeta.message}</p>
            )}
            {OPENAQ_REFERENCE_METRICS.includes(selectedMetric) && openaqMeta.status === 'error' && (
              <p className="text-cap text-amber-700">{openaqMeta.message}</p>
            )}
          </div>
          {weekCompareData.length ? (
            <ChartFrame
              height={260}
              xLabel="Day"
              yLabel={metricThemes[selectedMetric].unit}
              legendItems={[
                { label: 'Your data', color: theme.primary },
                {
                  label:
                    OPENAQ_REFERENCE_METRICS.includes(selectedMetric) &&
                    openaqMeta.status === 'ok' &&
                    openaqPoints?.length
                      ? (openaqMeta.message?.startsWith('WAQI')
                        ? 'Reference (WAQI)'
                        : 'Reference (OpenAQ)')
                      : 'Reference (simulated)',
                  color: '#94a3b8',
                },
              ]}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weekCompareData} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="label" {...AXIS_LINE_PROPS} />
                  <YAxis {...AXIS_LINE_PROPS} />
                  <Tooltip
                    contentStyle={{
                      background: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      padding: '12px',
                    }}
                  />
                  <Line type="monotone" dataKey="yours" name="Your data" stroke={theme.primary} strokeWidth={3} dot={{ r: 4 }} />
                  <Line
                    type="monotone"
                    dataKey="reference"
                    name="Reference"
                    stroke="#94a3b8"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartFrame>
          ) : (
            <p className="text-small text-muted py-8 text-center">Not enough dated points in this filter for a week chart.</p>
          )}
          <ReflectionPrompt storageKey={`week-vs-ref-${selectedMetric}`} mode="notice-wonder" />
        </div>
        )}

        {openSections.trends && (
        <div ref={monthChartRef} className="bg-surface rounded-card p-6 border border-hairline-soft">
          <div className="flex items-start justify-between gap-3 mb-1">
            <h2 className="text-tile text-fg">Your measurements over time</h2>
            <div className="flex items-center gap-1.5" data-export-hide="true">
              <SaveChartButton
                targetRef={monthChartRef}
                filename={`time-series-${metricThemes[selectedMetric].label}-${metricThemes[selectedMetric].unit}`}
              />
              <SendToWorkspaceButton
                onSendToWorkspace={onSendToWorkspace}
                buildItem={() => ({
                  id: `over-time-${selectedMetric}-${Date.now()}`,
                  title: `Your measurements over time — ${metricThemes[selectedMetric].label}`,
                  kind: 'line',
                  data: monthData,
                  xKey: 'date',
                  xLabel: 'Date',
                  yLabel: metricThemes[selectedMetric].unit,
                  series: [{ dataKey: 'value', label: 'Your data', color: theme.primary }],
                })}
              />
            </div>
          </div>
          <p className="text-cap text-muted mb-4">Daily average for the selected metric (all days in your current filter).</p>
          {monthData.length >= 2 ? (
            <ChartFrame height={260} xLabel="Date" yLabel={metricThemes[selectedMetric].unit}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthData} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis
                    dataKey="date"
                    style={{ fontSize: '10px' }}
                    stroke="#9CA3AF"
                    tickLine
                    axisLine={{ stroke: '#9CA3AF' }}
                    interval={4}
                  />
                  <YAxis {...AXIS_LINE_PROPS} />
                  <Tooltip
                    contentStyle={{
                      background: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      padding: '12px',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    name="Your data"
                    stroke={theme.primary}
                    strokeWidth={2}
                    dot={{ fill: theme.primary, r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartFrame>
          ) : (
            <p className="text-small text-muted py-12 text-center">
              Add more days of data (or relax filters) to see a time series.
            </p>
          )}
        </div>
        )}
      </div>

      {/* Distribution + box plot — paired side-by-side; each is a compact single-metric summary
          that doesn't need the full page width on its own. */}
      {(openSections.distribution || openSections.box) && (
      <div className={`grid grid-cols-1 gap-6 ${openSections.distribution && openSections.box ? 'lg:grid-cols-2' : ''}`}>
      {openSections.distribution && (
      <div ref={distributionChartRef} className="bg-surface rounded-card p-6 border border-hairline-soft">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h2 className="text-tile text-fg">Value Distribution</h2>
            <p className="text-cap text-muted">
              How your {metricThemes[selectedMetric].label} readings ({metricThemes[selectedMetric].unit}) spread across ranges.
            </p>
          </div>
          <div className="flex items-center gap-1.5" data-export-hide="true">
            <SaveChartButton
              targetRef={distributionChartRef}
              filename={`${metricThemes[selectedMetric].label}-${metricThemes[selectedMetric].unit}-value-distribution`}
            />
            <SendToWorkspaceButton
              onSendToWorkspace={onSendToWorkspace}
              buildItem={() => ({
                id: `distribution-${selectedMetric}-${Date.now()}`,
                title: `Value distribution — ${metricThemes[selectedMetric].label}`,
                kind: 'bar',
                xKey: 'range',
                xLabel: `${metricThemes[selectedMetric].label} range (${metricThemes[selectedMetric].unit})`,
                yLabel: 'Count',
                color: theme.primary,
                data: [
                  { range: '0-10', value: allValues.filter((v) => v >= 0 && v <= 10).length },
                  { range: '11-15', value: allValues.filter((v) => v > 10 && v <= 15).length },
                  { range: '16-20', value: allValues.filter((v) => v > 15 && v <= 20).length },
                  { range: '21-25', value: allValues.filter((v) => v > 20 && v <= 25).length },
                  { range: '26+', value: allValues.filter((v) => v > 25).length },
                ],
              })}
            />
          </div>
        </div>
        <ChartFrame
          height={260}
          xLabel={`${metricThemes[selectedMetric].label} range (${metricThemes[selectedMetric].unit})`}
          yLabel="Count"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={[
                { range: '0-10', count: allValues.filter((v) => v >= 0 && v <= 10).length },
                { range: '11-15', count: allValues.filter((v) => v > 10 && v <= 15).length },
                { range: '16-20', count: allValues.filter((v) => v > 15 && v <= 20).length },
                { range: '21-25', count: allValues.filter((v) => v > 20 && v <= 25).length },
                { range: '26+', count: allValues.filter((v) => v > 25).length },
              ]}
              margin={{ top: 8, right: 12, bottom: 4, left: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="range" {...AXIS_LINE_PROPS} />
              <YAxis {...AXIS_LINE_PROPS} />
              <Tooltip
                contentStyle={{
                  background: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  padding: '12px',
                }}
              />
              <Bar dataKey="count" fill={theme.primary} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartFrame>
      </div>
      )}

      {/* Box Plot — variability/distribution per team in the current class period */}
      {openSections.box && (
      <div ref={boxPlotRef} className="bg-surface rounded-card p-6 border border-hairline-soft">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-tile text-fg">Variability by team (box plot)</h2>
            <p className="text-cap text-muted">
              Min / Q1 / median / Q3 / max of {metricThemes[selectedMetric].label} for each team in your class period.
            </p>
          </div>
          {boxPlotGroups.length > 0 && (
            <div className="flex items-center gap-1.5" data-export-hide="true">
              <SaveChartButton
                targetRef={boxPlotRef}
                filename={`${metricThemes[selectedMetric].label}-${metricThemes[selectedMetric].unit}-box-plot-by-team`}
              />
              <SendToWorkspaceButton
                onSendToWorkspace={onSendToWorkspace}
                buildItem={() => ({
                  id: `box-${selectedMetric}-${Date.now()}`,
                  title: `Variability by team — ${metricThemes[selectedMetric].label}`,
                  kind: 'box',
                  groups: boxPlotGroups,
                  unit: metricThemes[selectedMetric].unit,
                  color: theme.primary,
                })}
              />
            </div>
          )}
        </div>
        <BoxPlot groups={boxPlotGroups} unit={metricThemes[selectedMetric].unit} color={theme.primary} />
      </div>
      )}
      </div>
      )}

      {/* Bivariate / scatter — relationship between two variables */}
      {openSections.scatter && (
      <div ref={scatterChartRef} className="bg-surface rounded-card p-6 border border-hairline-soft">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-tile text-fg">
              {metricThemes[selectedMetric].label} vs {metricThemes[scatterMetric].label}
            </h2>
            <p className="text-cap text-muted">Each point is one measurement, colored by team.</p>
          </div>
          <div className="flex shrink-0 items-center gap-2" data-export-hide="true">
            <span className="text-cap font-semibold text-muted">Compare with:</span>
            <select
              value={scatterMetric}
              onChange={(e) => setScatterMetric(e.target.value)}
              className="h-9 rounded-ctrl border border-hairline bg-surface px-3 text-small leading-normal"
            >
              {Object.entries(metricThemes)
                .filter(([key]) => key !== selectedMetric)
                .map(([key, m]) => (
                  <option key={key} value={key}>
                    {m.label}
                  </option>
                ))}
            </select>
            {scatterData.length > 0 && (
              <>
              <SaveChartButton
                targetRef={scatterChartRef}
                filename={`${metricThemes[selectedMetric].label}-${metricThemes[selectedMetric].unit}-vs-${metricThemes[scatterMetric].label}-${metricThemes[scatterMetric].unit}`}
              />
              <SendToWorkspaceButton
                onSendToWorkspace={onSendToWorkspace}
                buildItem={() => ({
                  id: `scatter-${selectedMetric}-${scatterMetric}-${Date.now()}`,
                  title: `${metricThemes[selectedMetric].label} vs ${metricThemes[scatterMetric].label}`,
                  kind: 'scatter',
                  data: scatterData,
                  xLabel: `${metricThemes[selectedMetric].label} (${metricThemes[selectedMetric].unit})`,
                  yLabel: `${metricThemes[scatterMetric].label} (${metricThemes[scatterMetric].unit})`,
                  color: theme.primary,
                })}
              />
              </>
            )}
          </div>
        </div>
        {scatterData.length ? (
          <ChartFrame
            height={280}
            xLabel={`${metricThemes[selectedMetric].label} (${metricThemes[selectedMetric].unit})`}
            yLabel={`${metricThemes[scatterMetric].label} (${metricThemes[scatterMetric].unit})`}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis
                  type="number"
                  dataKey="x"
                  name={metricThemes[selectedMetric].label}
                  {...AXIS_LINE_PROPS}
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  name={metricThemes[scatterMetric].label}
                  {...AXIS_LINE_PROPS}
                />
                <ZAxis range={[60, 60]} />
                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  contentStyle={{ background: 'white', border: 'none', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', padding: '12px' }}
                />
                <Scatter data={scatterData} fill={theme.primary} fillOpacity={0.7} />
              </ScatterChart>
            </ResponsiveContainer>
          </ChartFrame>
        ) : (
          <p className="text-small text-muted py-12 text-center">Not enough matching data points for a scatter plot yet.</p>
        )}
        <ReflectionPrompt storageKey={`scatter-${selectedMetric}-${scatterMetric}`} mode="cer" />
      </div>
      )}

      {/* Summary Insights */}
      {openSections.insights && (
      <div className="rounded-card p-8 border border-hairline-soft bg-surface">
        <h2 className="mb-4 flex items-center gap-2 text-tile text-fg">
          <Lightbulb className="h-5 w-5" style={{ color: theme.primary }} aria-hidden="true" />
          Key Insights
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-small font-semibold text-secondary mb-3">Statistical Summary:</h3>
            <ul className="space-y-2 text-small text-secondary">
              <li className="flex items-start gap-2">
                <span style={{ color: theme.primary }}>•</span>
                <span>Average {metricThemes[selectedMetric].label} is <strong>{avgValue} {metricThemes[selectedMetric].unit}</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: theme.primary }}>•</span>
                <span>Values range from <strong>{Math.round(minValue)}</strong> to <strong>{Math.round(maxValue)} {metricThemes[selectedMetric].unit}</strong></span>
              </li>
              {hasHealthThreshold(selectedMetric) && (
                <li className="flex items-start gap-2">
                  <span style={{ color: theme.primary }}>•</span>
                  <span>
                    Your average reads as <strong>{getStatusLabel(avgValue, selectedMetric)}</strong> on the health-threshold scale
                  </span>
                </li>
              )}
              {dailyOutliers.length > 0 && (
                <li className="flex items-start gap-2">
                  <span style={{ color: theme.primary }}>•</span>
                  <span>{dailyOutliers.length} surprising reading{dailyOutliers.length > 1 ? 's' : ''} flagged above — worth discussing</span>
                </li>
              )}
            </ul>
          </div>
          <div>
            <h3 className="text-small font-semibold text-secondary mb-3">Observations:</h3>
            <ul className="space-y-2 text-small text-secondary">
              <li className="flex items-start gap-2">
                <Check className="mt-0.5 h-4 w-4 flex-none text-green-600" aria-hidden="true" />
                <span>Data collected over {allValues.length} time points</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="mt-0.5 h-4 w-4 flex-none text-green-600" aria-hidden="true" />
                <span>Median value of {Math.round(medianValue)} shows central tendency</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="mt-0.5 h-4 w-4 flex-none text-green-600" aria-hidden="true" />
                <span>Open the <strong>Trends</strong> section above for the full daily series</span>
              </li>
            </ul>
          </div>
        </div>
        <ReflectionPrompt storageKey={`overview-summary-${selectedMetric}`} mode="cer" title="Claim, Evidence, Reasoning about this metric" />
      </div>
      )}
        </>
      ) : (
        /* Quick Compare View */
        <div className="space-y-6">
          {/* Quick Comparison Cards */}
          {/* Three parallel comparison cards — same neutral treatment for all three (no
              per-card rainbow of purple/blue borders and badges); only your own group's
              number uses the one chromatic accent, since it's the thing being compared against. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Your Group */}
            <div className="bg-surface rounded-card p-6 border border-hairline-soft">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-tile text-fg">Your Group</h3>
                <span className="px-2.5 py-1 bg-canvas border border-hairline text-secondary text-cap font-semibold rounded-pill">
                  G{filters.group.replace('G', '')}
                </span>
              </div>
              <div className="mb-4">
                <p className="text-4xl font-semibold mb-1" style={{ color: theme.primary }}>{avgValue}</p>
                <p className="text-small text-secondary">{metricThemes[selectedMetric].unit}</p>
              </div>
              <div className="space-y-2 text-small">
                <div className="flex justify-between">
                  <span className="text-secondary">Min</span>
                  <span className="font-semibold text-fg">{Math.round(minValue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary">Max</span>
                  <span className="font-semibold text-fg">{Math.round(maxValue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary">Range</span>
                  <span className="font-semibold text-fg">{Math.round(maxValue - minValue)}</span>
                </div>
              </div>
            </div>

            {/* Class Average */}
            <div className="bg-surface rounded-card p-6 border border-hairline-soft">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-tile text-fg">Class Average</h3>
                <span className="px-2.5 py-1 bg-canvas border border-hairline text-secondary text-cap font-semibold rounded-pill">
                  All Groups
                </span>
              </div>
              <div className="mb-4">
                <p className="text-4xl font-semibold text-fg mb-1">{classAverage ?? 'No data'}</p>
                {classAverage != null && (
                  <p className="text-small text-secondary">{metricThemes[selectedMetric].unit}</p>
                )}
              </div>
              <div className="space-y-2 text-small">
                <p className="text-cap text-muted">
                  Average across all groups in your class period (same school + period in imported data).
                </p>
                <div className="flex justify-between">
                  <span className="text-secondary">vs your group</span>
                  <span className="font-semibold text-fg">
                    {classAverage != null
                      ? avgValue <= classAverage
                        ? `${Math.abs(avgValue - classAverage)} lower`
                        : `${Math.abs(avgValue - classAverage)} higher`
                      : 'No data'}
                  </span>
                </div>
              </div>
            </div>

            {/* School Average */}
            <div className="bg-surface rounded-card p-6 border border-hairline-soft">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-tile text-fg">School Average</h3>
                <span className="px-2.5 py-1 bg-canvas border border-hairline text-secondary text-cap font-semibold rounded-pill">
                  {filters.school}
                </span>
              </div>
              <div className="mb-4">
                <p className="text-4xl font-semibold text-fg mb-1">{schoolAverage ?? 'No data'}</p>
                {schoolAverage != null && (
                  <p className="text-small text-secondary">{metricThemes[selectedMetric].unit}</p>
                )}
              </div>
              <div className="space-y-2 text-small">
                <p className="text-cap text-muted">
                  Average for your school code across imported rows (all classes/groups in file).
                </p>
                <div className="flex justify-between">
                  <span className="text-secondary">vs your group</span>
                  <span className="font-semibold text-fg">
                    {schoolAverage != null
                      ? avgValue <= schoolAverage
                        ? `${Math.abs(avgValue - schoolAverage)} lower`
                        : `${Math.abs(avgValue - schoolAverage)} higher`
                      : 'No data'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Chart */}
          <div ref={quickCompareChartRef} className="bg-surface rounded-card p-6 border border-hairline-soft">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <div>
                <h3 className="text-tile text-fg mb-1">Your recent week comparison</h3>
                <p className="text-cap text-muted">
                  Compare your filtered data with OpenAQ, another group, class average, or school average.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2" data-export-hide="true">
                <select
                  value={compareMode}
                  onChange={(e) => setCompareMode(e.target.value)}
                  className="sel-sm h-9 rounded-ctrl border border-hairline bg-surface px-3 text-small leading-normal"
                >
                  <option value="openaq">vs OpenAQ reference</option>
                  <option value="group">vs another group</option>
                  <option value="class">vs class average</option>
                  <option value="school">vs school average</option>
                </select>
                {compareMode === 'group' && (
                  <select
                    value={compareGroup}
                    onChange={(e) => setCompareGroup(e.target.value)}
                    className="sel-sm h-9 text-small border border-hairline rounded-ctrl px-3 bg-surface"
                  >
                    {availableCompareGroups.length ? (
                      availableCompareGroups.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))
                    ) : (
                      <option value="">NO DATA</option>
                    )}
                  </select>
                )}
                <SaveChartButton
                  targetRef={quickCompareChartRef}
                  filename={`${metricThemes[selectedMetric].label}-${metricThemes[selectedMetric].unit}-quick-compare-${compareMode}`}
                />
                <SendToWorkspaceButton
                  onSendToWorkspace={onSendToWorkspace}
                  buildItem={() => ({
                    id: `quick-compare-${compareMode}-${selectedMetric}-${Date.now()}`,
                    title: `Quick compare (${compareMode}) — ${metricThemes[selectedMetric].label}`,
                    kind: 'line',
                    data: compareChartData,
                    xKey: 'day',
                    xLabel: 'Day',
                    yLabel: metricThemes[selectedMetric].unit,
                    series: [
                      { dataKey: 'yours', label: 'Your data', color: theme.primary },
                      { dataKey: 'comparison', label: 'Comparison', color: '#64748b' },
                    ],
                  })}
                />
              </div>
            </div>
            <ChartFrame
              height={300}
              xLabel="Day"
              yLabel={metricThemes[selectedMetric].unit}
              legendItems={[
                { label: 'Your data', color: theme.primary },
                {
                  label:
                    compareMode === 'openaq'
                      ? 'OpenAQ / reference'
                      : compareMode === 'group'
                        ? `Group ${compareGroup || ''}`
                        : compareMode === 'class'
                          ? 'Class average'
                          : 'School average',
                  color: '#64748b',
                },
              ]}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={compareChartData} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="day" {...AXIS_LINE_PROPS} />
                  <YAxis {...AXIS_LINE_PROPS} />
                  <Tooltip
                    contentStyle={{
                      background: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      padding: '12px',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="yours"
                    name="Your data"
                    stroke={theme.primary}
                    strokeWidth={3}
                    dot={{ fill: theme.primary, r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="comparison"
                    name="Comparison"
                    stroke="#64748b"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: '#64748b', r: 4 }}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartFrame>
            <ReflectionPrompt storageKey={`quick-compare-${compareMode}-${selectedMetric}`} mode="cer" />
          </div>

          {/* Insights — plain neutral cards, no gradients or colored bullets. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-surface rounded-card p-6 border border-hairline-soft">
              <h3 className="text-tile text-fg mb-3">Quick read</h3>
              <ul className="space-y-2 text-small text-secondary">
                <li>
                  <strong className="text-fg">Your group</strong> average for this metric: {avgValue} {metricThemes[selectedMetric].unit}.
                </li>
                <li>
                  {classAverage != null
                    ? `Class-wide (same period) average is ${classAverage} ${metricThemes[selectedMetric].unit}.`
                    : 'Class average needs more imported rows (other groups in the same period).'}
                </li>
              </ul>
            </div>

            <div className="bg-surface rounded-card p-6 border border-hairline-soft">
              <h3 className="text-tile text-fg mb-3">Compare further</h3>
              <ul className="space-y-2 text-small text-secondary">
                <li>
                  On <strong className="text-fg">Overview</strong>, compare the recent week with Philadelphia, New York, or Hanoi.
                </li>
                <li>Use the compare mode selector above to switch between OpenAQ, other groups, class, and school.</li>
              </ul>
            </div>
          </div>

          {/* CTA for Full Comparison — a plain card with a normal primary button, not a
              full-bleed colored banner. */}
          <div className="bg-surface rounded-card p-6 border border-hairline-soft flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="text-tile text-fg mb-1">Want to explore more comparisons?</h3>
              <p className="text-small text-muted">Compare with other schools, locations, and time periods</p>
            </div>
            <Button onClick={() => setShowCompareModal(true)}>
              <GitCompareArrows className="h-4 w-4" aria-hidden="true" />
              Open detailed comparison
            </Button>
          </div>
        </div>
      )}

      {/* Modals */}
      <ComparisonModal
        isOpen={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        selectedMetric={selectedMetric}
        theme={theme}
        metricThemes={metricThemes}
        currentFilters={filters}
        workspaceGroups={workspaceGroupsForCompare}
        comparisonSchoolCodes={comparisonSchoolCodes}
        importedRows={imported}
      />
    </div>
  );
};

export default AnalysisView;